import React from 'react';
import { Link } from 'react-router-dom';
import { SliderModal } from '@components/molecules/SlideOver';
import UsersInsideAreaList from '@components/organisms/UsersInsideAreaList';
import GoogleMapsLinkButton from '@components/atoms/GoogleMapsLinkButton';
import { UsersInsideAreaFilter } from '@models/usersInsideArea.model';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import LaunchIcon from '@mui/icons-material/Launch';

interface UsersInsideAreaDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** What we're showing — can be project-scoped, geofence-scoped, or both */
  filter: UsersInsideAreaFilter | null;
  /** Optional title shown next to the icon (e.g. project name or geofence name) */
  areaName?: string;
  /** Optional subtitle (e.g. "Project · Downtown Site") */
  areaSubtitle?: string;
  /** When provided, renders quick links to the project (Dashboard, Schedule). */
  relatedProjectId?: string | null;
  /** When provided, renders a quick link to the warehouse settings. */
  relatedWarehouseId?: string | null;
  /** Coordinates of the area — used to render a Google Maps directions link. */
  lat?: number | null;
  lng?: number | null;
}

export const UsersInsideAreaDrawer: React.FC<UsersInsideAreaDrawerProps> = ({
  isOpen,
  onClose,
  filter,
  areaName,
  areaSubtitle,
  relatedProjectId,
  relatedWarehouseId,
  lat,
  lng,
}) => {
  const hasCoords =
    typeof lat === 'number' && typeof lng === 'number';
  const title = (
    <div className="flex items-center gap-2">
      <GroupOutlinedIcon className="text-orange-400" sx={{ fontSize: 24 }} />
      <span>People inside</span>
    </div>
  );

  // Reset key — anything that changes the area should reset list state.
  const resetKey = isOpen
    ? `${filter?.projectId ?? ''}|${filter?.geofenceId ?? ''}`
    : null;

  return (
    <SliderModal
      isOpen={isOpen}
      closeSlider={onClose}
      title={title}
      cancelText="Close"
    >
      {(areaName ||
        areaSubtitle ||
        relatedProjectId ||
        relatedWarehouseId ||
        hasCoords) && (
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          {areaName && (
            <p className="text-sm font-semibold text-gray-900">{areaName}</p>
          )}
          {areaSubtitle && (
            <p className="text-xs text-gray-500 mt-0.5">{areaSubtitle}</p>
          )}
          {(relatedProjectId || relatedWarehouseId) && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs">
              {relatedProjectId && (
                <>
                  <Link
                    to={`/projects/${relatedProjectId}`}
                    className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    Project Dashboard
                    <LaunchIcon sx={{ fontSize: 12 }} />
                  </Link>
                  <Link
                    to={`/schedule/${relatedProjectId}`}
                    className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    Project Tasks / Schedule
                    <LaunchIcon sx={{ fontSize: 12 }} />
                  </Link>
                </>
              )}
              {relatedWarehouseId && (
                <Link
                  to={`/warehouse/manage`}
                  className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  Warehouse settings
                  <LaunchIcon sx={{ fontSize: 12 }} />
                </Link>
              )}
            </div>
          )}
          {hasCoords && (
            <div className="mt-2">
              <GoogleMapsLinkButton
                lat={lat}
                lng={lng}
                label="Copy directions"
              />
            </div>
          )}
        </div>
      )}

      <div className="pb-20">
        <UsersInsideAreaList
          filter={filter}
          enabled={isOpen}
          resetKey={resetKey}
        />
      </div>
    </SliderModal>
  );
};

export default UsersInsideAreaDrawer;
